import { 
  FileText, 
  BookOpen, 
  Calculator, 
  FileSpreadsheet, 
  Download, 
  Upload, 
  Edit, 
  CheckCircle, 
  Clock,
  Users,
  Inbox
} from 'lucide-react';
import { Card } from '../ui/Card';
import { useActivities, ActivityEvent } from '../../hooks/useActivityTracking';

const getActivityIcon = (type: ActivityEvent['type']) => {
  const iconClass = "h-4 w-4 flex-shrink-0";
  
  switch (type) {
    case 'journal':
      return <FileText className={iconClass} />;
    case 'ledger':
      return <BookOpen className={iconClass} />;
    case 'declaration':
      return <FileSpreadsheet className={iconClass} />;
    case 'export':
      return <Download className={iconClass} />;
    case 'import':
      return <Upload className={iconClass} />;
    case 'validation':
      return <CheckCircle className={iconClass} />;
    case 'calculation':
      return <Calculator className={iconClass} />;
    case 'user_action':
      return <Users className={iconClass} />;
    default:
      return <Edit className={iconClass} />;
  }
};

const getStatusColor = (status: ActivityEvent['status']) => {
  switch (status) {
    case 'success':
      return 'text-green-600 bg-green-50 dark:bg-green-900/20';
    case 'warning':
      return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
    case 'error':
      return 'text-red-600 bg-red-50 dark:bg-red-900/20';
    case 'pending':
      return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20';
    default:
      return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20';
  }
};

const getTimeAgo = (timestamp: Date) => {
  const now = new Date();
  const diffInMs = now.getTime() - timestamp.getTime();
  const diffInMin = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInMin < 1) {
    return 'À l\'instant';
  } else if (diffInMin < 60) {
    return `Il y a ${diffInMin} min`;
  } else if (diffInHours < 24) {
    return `Il y a ${diffInHours}h`;
  } else if (diffInDays < 7) {
    return `Il y a ${diffInDays}j`;
  } else {
    return timestamp.toLocaleDateString('fr-FR');
  }
};

export function RecentActivitiesCard() {
  const { activities, loading, getRecentActivities } = useActivities();
  
  const recentActivities = getRecentActivities(8);

  // Afficher un loader pendant le chargement
  if (loading) {
    return (
      <Card title="Activités Récentes" className="h-[500px]">
        <div className="h-[420px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Card>
    );
  }

  // Afficher un message si aucune activité
  if (recentActivities.length === 0) {
    return (
      <Card title="Activités Récentes" className="h-[500px]">
        <div className="h-[420px] flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
          <Inbox className="h-12 w-12 mb-4 opacity-50" />
          <p className="text-lg font-medium mb-2">Aucune activité récente</p>
          <p className="text-sm text-center max-w-xs">
            Les activités apparaîtront ici lorsque vous effectuerez des actions dans l'application.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Activités Récentes" className="h-[500px]">
      <div className="h-[420px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
        <div className="space-y-3 pr-2">
          {recentActivities.map((activity: ActivityEvent) => (
            <div
              key={activity.id}
              className="flex items-start space-x-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-150"
            >
              {/* Icône et statut */}
              <div className={`p-2 rounded-full ${getStatusColor(activity.status)}`}>
                {getActivityIcon(activity.type)}
              </div>
              
              {/* Contenu */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {activity.action}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {activity.description}
                    </p>
                  </div>
                  
                  {/* Badge du module */}
                  <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full whitespace-nowrap">
                    {activity.module}
                  </span>
                </div>
                
                {/* Footer avec utilisateur et timestamp */}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                    <Users className="h-3 w-3" />
                    <span>{activity.user || 'Système'}</span>
                  </div>
                  <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                    <Clock className="h-3 w-3" />
                    <span>{getTimeAgo(activity.timestamp)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Footer avec compteur d'activités */}
      <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          {activities.length} activité{activities.length > 1 ? 's' : ''} enregistrée{activities.length > 1 ? 's' : ''}
        </p>
      </div>
    </Card>
  );
}
